import { LitElement, html, css } from "lit";
import { property, customElement } from "lit/decorators.js";
import "@maersk-global/mds-components-core/mc-toast";
import "@maersk-global/mds-components-core/mc-notification";
import "@maersk-global/mds-foundations/css/foundations.css";
import { NotificationType } from "../../src/services/types/fleet-notification-service.types";
import {
  NotificationEvents,
  VoyFleetNotificationModel,
} from "../../src/models/voy-fleet-notification-model";
import { VesselsModel } from "../../src/models/vessels-model";
import { GetFormattedDateNoYear } from "../../src/utils/helpers";
import {
  FiSubView,
  UiStateModel,
  UiStateModelParam,
} from "../../src/models/ui-state-model";
import { AlarmState } from "../../src/services/types/fleet-alarms-service.types";

class NotificationCount extends LitElement {
  @property({ type: Number }) count = 0;

  static get styles() {
    return css`
      .count {
        background: #f9d044;
        font-family: MaerskText;
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
        color: white;
        text-align: center;
        position: absolute;
        right: 0px;
        top: 0px;
        transform: translate(50%, -50%);
        border-radius: 50%;
        width: 15px;
        height: 15px;
        padding: 2px;
      }
    `;
  }

  render() {
    return html`<div class="count">${this.count}</div>`;
  }
}
customElements.define("notification-count", NotificationCount);

@customElement("voy-offline-vessels-popup")
export class VoyOfflineVesselsPopup extends LitElement {
  @property({ attribute: false }) private notifications?:
    | NotificationType[]
    | null = [];
  @property({ attribute: false }) private vesselMap = new Map();

  constructor() {
    super();
    this._checkForOfflineVesselNotification =
      this._checkForOfflineVesselNotification.bind(this);
    this._handleVesselsUpdated = this._handleVesselsUpdated.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this._loadVesselData();

    VesselsModel!.addEventListener(
      "vessels-updated",
      this._handleVesselsUpdated
    );
    VoyFleetNotificationModel!.addEventListener(
      NotificationEvents.OfflineNotificationsUpdated,
      this._checkForOfflineVesselNotification
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    VesselsModel!.removeEventListener(
      "vessels-updated",
      this._handleVesselsUpdated
    );
    VoyFleetNotificationModel!.removeEventListener(
      NotificationEvents.OfflineNotificationsUpdated,
      this._checkForOfflineVesselNotification
    );
  }

  private _handleVesselsUpdated() {
    this._loadVesselData();
    this.requestUpdate();
  }

  private _loadVesselData() {
    if (this.notifications && this.notifications.length > 0) {
      this.notifications.forEach((notification) => {
        const vessel = VesselsModel?.getVessel(notification.imo_no);
        if (vessel) {
          this.vesselMap.set(notification.imo_no, vessel);
        }
      });
    }
  }

  private async _checkForOfflineVesselNotification() {
    this.notifications =
      VoyFleetNotificationModel?.offlineVesselNotifications || [];
    this._loadVesselData();
    this.requestUpdate();
  }

  private _getNotificationText({ imo_no, created_at }: NotificationType) {
    const formattedDate = GetFormattedDateNoYear(created_at);
    const vessel = this.vesselMap.get(imo_no);
    const vesselName = vessel?.vesselName || `Vessel`;

    return `Cloud Vessel Offline | ${vesselName} IMO(${imo_no}) | ${formattedDate}`;
  }

  private _markNotificationAsRead(notificationId: string) {
    try {
      const payload = {
        data: JSON.stringify("Acknowledged by VET"),
        state: AlarmState.InProgress,
      };
      VoyFleetNotificationModel?.updateNotification(
        notificationId,
        payload,
        "vesseloffline"
      );
    } catch (error) {
      console.error("Error updating notification state:", error);
    }
  }

  deeplinkToVesselOverView({
    event,
    imo_no,
    notificationId,
    index,
  }: {
    event: Event;
    imo_no: number;
    notificationId: string;
    index: number;
  }) {
    event.stopPropagation();

    UiStateModel!.setValue(UiStateModelParam.IMO, imo_no.toString());
    UiStateModel!.setValue(UiStateModelParam.PORT);
    UiStateModel!.setValue(UiStateModelParam.PortRegion);
    UiStateModel!.setValue(
      UiStateModelParam.FISubView,
      FiSubView.VesselDetails
    );
    UiStateModel!.setLeftPanelVisibility(true, "hide-anchor");

    this._markNotificationAsRead(notificationId);

    //need to manually close the toast in this scenario
    const mcToast = this.shadowRoot?.querySelectorAll("mc-toast");
    (mcToast?.[index] as any)?.hide();
  }

  render() {
    // waiting for the vessels data to be loaded so that the vessel name can be displayed simultaneously
    const vesselsDataLoaded =
      (VesselsModel?.maerskVessels && VesselsModel?.maerskVessels.size > 0) ||
      false;

    if (!vesselsDataLoaded) {
      return html``;
    }

    // filtering out out read notifications
    const unreadNotifications =
      this.notifications?.filter(
        (notification: any) =>
          notification.state.toLowerCase() === AlarmState.Active
      ) || [];
    const undreadCount = unreadNotifications.length;

    return html`
      ${unreadNotifications.map(
        (notification, index) => html`
          <mc-toast
            open
            class="container"
            position="top-center"
            appearance="warning"
            duration="99999999"
            width="auto"
            @close=${() => this._markNotificationAsRead(notification.id)}
          >
            <mc-notification
              style=${`
              margin-top: 49px;
              position: fixed;
              box-shadow: none;
              border: 1px solid #f9d044;
              top: ${index === 0 ? "0px" : "10px"};
              z-index: ${index === 0 ? "1" : "0"};
              width: 600px;
              
            `}
            >
              ${index === 0 && undreadCount > 1
                ? html`<notification-count
                    .count=${undreadCount}
                  ></notification-count>`
                : null}
              <div style="margin-bottom: 2px;">
                <b>${this._getNotificationText(notification)}</b>
              </div>
              <p>
                The vessel has been offline for 3 days and will submit a passage
                plan via Basic 2.0.
              </p>
              <mc-button
                variant="plain"
                appearance="neutral"
                padding="none"
                style="text-decoration: underline; margin-top: 4px;"
                @click=${(e: Event) =>
                  this.deeplinkToVesselOverView({
                    event: e,
                    imo_no: notification.imo_no,
                    notificationId: notification.id,
                    index,
                  })}
                >View vessel details</mc-button
              >
            </mc-notification></mc-toast
          >
        `
      )}
    `;
  }
}
